import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  alpha,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
} from '@mui/material';
import {
  People,
  Business,
  Category,
  School,
  Folder,
  Event,
  VpnKey,
  Gavel,
  Info,
  Collections,
  Groups,
  Handshake,
  Description,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import StatCard from '../components/StatCard';
import RecentActivity from '../components/RecentActivity';

// API URL configuration
const API_URL = import.meta.env.VITE_API_URL === '' ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

// Get auth token helper
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    companies: 0,
    authPeople: 0,
    trainings: 0,
    files: 0,
    tenders: 0,
    news: 0,
    events: 0,
    partners: 0,
    contracts: 0,
    boardMeetings: 0,
    galleryPhotos: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch counts from Supabase tables directly (non-auth tables)
      const [companies, trainings, files, tenders, news, events, partners, contracts, boardMeetings, galleryPhotos] = await Promise.all([
        supabase.from('companies').select('id', { count: 'exact', head: true }),
        supabase.from('trainings').select('id', { count: 'exact', head: true }),
        supabase.from('documents').select('id', { count: 'exact', head: true }),
        supabase.from('tenders').select('id', { count: 'exact', head: true }),
        supabase.from('news').select('id', { count: 'exact', head: true }),
        supabase.from('events').select('id', { count: 'exact', head: true }),
        supabase.from('partners').select('id', { count: 'exact', head: true }),
        supabase.from('contracts').select('id', { count: 'exact', head: true }),
        supabase.from('board_meetings').select('id', { count: 'exact', head: true }),
        supabase.from('gallery_photos').select('id', { count: 'exact', head: true }),
      ]);

      // Fetch auth users count from backend API stats endpoint (only auth uses backend)
      let authPeopleCount = 0;
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_URL}/api/stats`, { headers });
        if (response.ok) {
          const result = await response.json();
          authPeopleCount = result.data?.users || 0;
        }
      } catch (error) {
        console.error('Error fetching auth users count:', error);
      }

      setStats({
        companies: companies.count || 0,
        authPeople: authPeopleCount,
        trainings: trainings.count || 0,
        files: files.count || 0,
        tenders: tenders.count || 0,
        news: news.count || 0,
        events: events.count || 0,
        partners: partners.count || 0,
        contracts: contracts.count || 0,
        boardMeetings: boardMeetings.count || 0,
        galleryPhotos: galleryPhotos.count || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('nav.contractors'),
      value: stats.companies,
      icon: <People sx={{ fontSize: 24 }} />,
      colorKey: 'primary' as const,
      path: '/contractors',
    },
    {
      title: t('nav.authSystem'),
      value: stats.authPeople,
      icon: <VpnKey sx={{ fontSize: 24 }} />,
      colorKey: 'success' as const,
      path: '/auth-system',
    },
    {
      title: t('nav.training'),
      value: stats.trainings,
      icon: <School sx={{ fontSize: 24 }} />,
      colorKey: 'warning' as const,
      path: '/trainings',
    },
    {
      title: t('nav.files'),
      value: stats.files,
      icon: <Folder sx={{ fontSize: 24 }} />,
      colorKey: 'info' as const,
      path: '/files',
    },
    {
      title: t('nav.tenders'),
      value: stats.tenders,
      icon: <Gavel sx={{ fontSize: 24 }} />,
      colorKey: 'error' as const,
      path: '/tenders',
    },
    {
      title: t('nav.news'),
      value: stats.news,
      icon: <Info sx={{ fontSize: 24 }} />,
      colorKey: 'primary' as const,
      path: '/news',
    },
    {
      title: t('nav.gallery'),
      value: stats.galleryPhotos,
      icon: <Collections sx={{ fontSize: 24 }} />,
      colorKey: 'secondary' as const,
      path: '/gallery',
    },
    {
      title: t('nav.boardMeetings'),
      value: stats.boardMeetings,
      icon: <Groups sx={{ fontSize: 24 }} />,
      colorKey: 'info' as const,
      path: '/board-meetings',
    },
    {
      title: t('nav.events'),
      value: stats.events,
      icon: <Event sx={{ fontSize: 24 }} />,
      colorKey: 'warning' as const,
      path: '/events',
    },
    {
      title: t('nav.partners'),
      value: stats.partners,
      icon: <Handshake sx={{ fontSize: 24 }} />,
      colorKey: 'success' as const,
      path: '/partners',
    },
    {
      title: t('nav.contracts'),
      value: stats.contracts,
      icon: <Description sx={{ fontSize: 24 }} />,
      colorKey: 'error' as const,
      path: '/contracts',
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            {t('welcome')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('manageDashboard')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/ai-agent')}
          startIcon={<AutoAwesome />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, whiteSpace: 'nowrap', flexShrink: 0, borderRadius: 2, boxShadow: 2 }}
        >
          {t('agent.talkCta')}
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {statCards.map((stat) => (
          <Box key={stat.title} sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: '180px' }}>
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              colorKey={stat.colorKey}
              onClick={() => navigate(stat.path)}
            />
          </Box>
        ))}
      </Box>

      {/* Recent Activity / Change Log */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <RecentActivity compact />
          </CardContent>
        </Card>
      </Box>

      {/* Quick Links and Activity */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Quick Links */}
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '300px' }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" gutterBottom>
                {t('dashboardSections.quickLinks')}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {([
                  { label: t('nav.contractors'), icon: <People />, path: '/contractors', color: 'primary' },
                  { label: t('nav.companies'), icon: <Business />, path: '/companies', color: 'secondary' },
                  { label: t('nav.classifications'), icon: <Category />, path: '/classifications', color: 'warning' },
                  { label: t('nav.training'), icon: <School />, path: '/trainings', color: 'success' },
                  { label: t('nav.files'), icon: <Folder />, path: '/files', color: 'info' },
                  { label: t('nav.events'), icon: <Event />, path: '/events', color: 'error' },
                ] as const).map((link) => (
                  <Box key={link.label} sx={{ flex: '1 1 calc(50% - 6px)', minWidth: '130px' }}>
                    <Card
                      onClick={() => navigate(link.path)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        height: '100%',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette[link.color].main, 0.04),
                          transform: 'scale(1.02)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              bgcolor: (theme) => alpha(theme.palette[link.color].main, 0.1),
                              color: `${link.color}.main`,
                              p: 0.75,
                              borderRadius: 1,
                              display: 'flex',
                            }}
                          >
                            {React.cloneElement(link.icon, { sx: { fontSize: 20 } })}
                          </Box>
                          <Typography variant="body2" fontWeight={500}>
                            {link.label}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Recent Activity */}
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '300px' }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" gutterBottom>
                {t('dashboardSections.systemStatus')}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <List disablePadding>
                {[
                  { label: t('dashboardSections.database'), status: t('dashboardSections.online'), color: 'success' },
                  { label: t('dashboardSections.authentication'), status: t('dashboardSections.active'), color: 'success' },
                  { label: t('dashboardSections.storage'), status: t('dashboardSections.healthy'), color: 'success' },
                  { label: t('dashboardSections.api'), status: t('dashboardSections.operational'), color: 'success' },
                ].map((item, index) => (
                  <ListItem
                    key={item.label}
                    divider={index < 3}
                    sx={{ px: 0, py: 1 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                          color: `${item.color}.main`,
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem',
                        }}
                      >
                        {item.label.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 500, fontSize: '0.8125rem' }}
                    />
                    <Chip
                      label={item.status}
                      size="small"
                      color={item.color as any}
                      sx={{ fontWeight: 500, height: 24, fontSize: '0.75rem' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
